import * as React from "react";
import { Toast, ToastProps } from "./Toast";
import { cn } from "../../lib/utils";

export interface ToastOptions extends Omit<ToastProps, "isVisible" | "id"> {
  /**
   * Unique identifier for the toast (auto-generated if not provided)
   */
  id?: string;
}

export interface ToastInstance extends ToastOptions {
  id: string;
  isVisible: boolean;
}

interface ToastContextType {
  /**
   * Show an information toast
   */
  showToast: (options: ToastOptions) => string;
  /**
   * Show a success toast (shorthand for positive intent)
   */
  success: (message: string, options?: Partial<ToastOptions>) => string;
  /**
   * Show an error toast (shorthand for negative intent)
   */
  error: (message: string, options?: Partial<ToastOptions>) => string;
  /**
   * Show a warning toast (shorthand for notice intent)
   */
  warning: (message: string, options?: Partial<ToastOptions>) => string;
  /**
   * Show an info toast (shorthand for info intent)
   */
  info: (message: string, options?: Partial<ToastOptions>) => string;
  /**
   * Show a promotional toast
   */
  promotional: (
    heading: string,
    description: string,
    options?: Partial<ToastOptions>
  ) => string;
  /**
   * Dismiss a specific toast by ID
   */
  dismiss: (id: string) => void;
  /**
   * Dismiss all toasts
   */
  dismissAll: () => void;
}

const ToastContext = React.createContext<ToastContextType | undefined>(
  undefined
);

export interface ToastProviderProps {
  children: React.ReactNode;
  /**
   * Maximum number of toasts to display at once
   * @default 5
   */
  maxToasts?: number;
  /**
   * Default duration for information toasts in milliseconds
   * @default 5000
   */
  defaultDuration?: number;
}

const positionClasses = {
  "top-left": "top-4 left-4",
  "top-center": "top-4 left-1/2 -translate-x-1/2",
  "top-right": "top-4 right-4",
  "bottom-left": "bottom-4 left-4",
  "bottom-center": "bottom-4 left-1/2 -translate-x-1/2",
  "bottom-right": "bottom-4 right-4",
};

const getDefaultPosition = (
  type?: "information" | "promotional"
): "bottom-center" | "bottom-right" => {
  return type === "promotional" ? "bottom-right" : "bottom-center";
};

const getAnimationClasses = (position: string, isVisible: boolean): string => {
  const isTop = position.startsWith("top");
  const isBottom = position.startsWith("bottom");

  if (!isVisible) {
    // Exit/hidden state - slide out
    if (isTop) {
      return "-translate-y-[120%] pointer-events-none";
    }
    if (isBottom) {
      return "translate-y-[120%] pointer-events-none";
    }
    // Fallback
    return "translate-y-[120%] pointer-events-none";
  }

  // Visible state - slide in
  return "translate-y-0";
};

export const ToastProvider: React.FC<ToastProviderProps> = ({
  children,
  maxToasts = 5,
  defaultDuration = 5000,
}) => {
  const [toasts, setToasts] = React.useState<ToastInstance[]>([]);
  const toastCounter = React.useRef(0);

  const generateId = () => {
    toastCounter.current += 1;
    return `toast-${Date.now()}-${toastCounter.current}`;
  };

  const showToast = React.useCallback(
    (options: ToastOptions): string => {
      const id = options.id || generateId();
      const toastType = options.type || "information";

      const newToast: ToastInstance = {
        ...options,
        id,
        isVisible: false, // Start hidden to trigger entry animation
        type: toastType,
        position: options.position || getDefaultPosition(toastType),
        duration:
          options.duration ??
          (toastType === "promotional" ? 0 : defaultDuration),
      };

      setToasts((prevToasts) => {
        const filtered = prevToasts.filter((t) => t.id !== id);
        const updated = [...filtered, newToast];
        // Keep only the most recent maxToasts
        return updated.slice(-maxToasts);
      });

      // Trigger entry animation after DOM update
      // Use setTimeout to ensure the initial hidden state is rendered first
      setTimeout(() => {
        setToasts((prevToasts) =>
          prevToasts.map((toast) =>
            toast.id === id ? { ...toast, isVisible: true } : toast
          )
        );
      }, 10);

      return id;
    },
    [maxToasts, defaultDuration]
  );

  const dismiss = React.useCallback((id: string) => {
    setToasts((prevToasts) =>
      prevToasts.map((toast) =>
        toast.id === id ? { ...toast, isVisible: false } : toast
      )
    );

    // Remove toast after animation completes (500ms animation + 50ms buffer)
    setTimeout(() => {
      setToasts((prevToasts) => prevToasts.filter((toast) => toast.id !== id));
    }, 550);
  }, []);

  const dismissAll = React.useCallback(() => {
    setToasts((prevToasts) =>
      prevToasts.map((toast) => ({ ...toast, isVisible: false }))
    );

    // Remove all toasts after animation completes (500ms animation + 50ms buffer)
    setTimeout(() => {
      setToasts([]);
    }, 550);
  }, []);

  const success = React.useCallback(
    (message: string, options?: Partial<ToastOptions>): string => {
      return showToast({
        type: "information",
        intent: "positive",
        message,
        ...options,
      });
    },
    [showToast]
  );

  const error = React.useCallback(
    (message: string, options?: Partial<ToastOptions>): string => {
      return showToast({
        type: "information",
        intent: "negative",
        message,
        ...options,
      });
    },
    [showToast]
  );

  const warning = React.useCallback(
    (message: string, options?: Partial<ToastOptions>): string => {
      return showToast({
        type: "information",
        intent: "notice",
        message,
        ...options,
      });
    },
    [showToast]
  );

  const info = React.useCallback(
    (message: string, options?: Partial<ToastOptions>): string => {
      return showToast({
        type: "information",
        intent: "info",
        message,
        ...options,
      });
    },
    [showToast]
  );

  const promotional = React.useCallback(
    (
      heading: string,
      description: string,
      options?: Partial<ToastOptions>
    ): string => {
      return showToast({
        type: "promotional",
        heading,
        description,
        ...options,
      });
    },
    [showToast]
  );

  const contextValue = React.useMemo(
    () => ({
      showToast,
      success,
      error,
      warning,
      info,
      promotional,
      dismiss,
      dismissAll,
    }),
    [showToast, success, error, warning, info, promotional, dismiss, dismissAll]
  );

  // Group toasts by position
  const toastsByPosition = React.useMemo(() => {
    const grouped: Record<string, ToastInstance[]> = {};
    toasts.forEach((toast) => {
      const pos = toast.position || getDefaultPosition(toast.type);
      if (!grouped[pos]) {
        grouped[pos] = [];
      }
      grouped[pos].push(toast);
    });
    return grouped;
  }, [toasts]);

  return (
    <ToastContext.Provider value={contextValue}>
      {children}
      {/* Toast Containers - one for each position */}
      {(
        Object.keys(toastsByPosition) as Array<keyof typeof positionClasses>
      ).map((position) => {
        const positionToasts = toastsByPosition[position];
        if (!positionToasts) return null;

        return (
          <div
            key={position}
            className={`fixed z-10000 flex flex-col gap-3 ${positionClasses[position]}`}
            aria-live="polite"
            aria-atomic="false"
          >
            {positionToasts.map((toast: ToastInstance) => {
              const toastPosition =
                toast.position || getDefaultPosition(toast.type);
              const animationClasses = getAnimationClasses(
                toastPosition,
                toast.isVisible
              );
              return (
                <div
                  key={toast.id}
                  className={cn(
                    "will-change-transform transition-transform duration-200 ease-out",
                    animationClasses
                  )}
                >
                  <Toast
                    {...toast}
                    isVisible={toast.isVisible}
                    onClose={() => {
                      toast.onClose?.();
                      dismiss(toast.id);
                    }}
                  />
                </div>
              );
            })}
          </div>
        );
      })}
    </ToastContext.Provider>
  );
};

/**
 * Hook to access toast functionality
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const toast = useToast();
 *
 *   return (
 *     <button onClick={() => toast.success("Saved successfully!")}>
 *       Save
 *     </button>
 *   );
 * }
 * ```
 */
export const useToast = (): ToastContextType => {
  const context = React.useContext(ToastContext);
  if (!context) {
    throw new Error("useToast must be used within a ToastProvider");
  }
  return context;
};
